import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ExternalLink } from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'
import { GlassCard } from '@/components/ui/GlassCard'
import { BeforeAfterSlider } from './BeforeAfterSlider'
import { MediaTabs } from './MediaTabs'
import { CaseStudyAccordion } from './CaseStudyAccordion'
import type { PortfolioProject } from '@/lib/portfolio'

/**
 * On mobile (`<md`), the description/chips/link/case-study block is a big
 * scroll per project (name, description, tag chips, external link, then
 * the full Problem/Solution/Result accordion) — this session's request
 * collapses all of that behind one toggle ("Vidi detalje"/"View details"),
 * closed by default, so scanning the portfolio on a phone doesn't mean
 * scrolling through every project's full case study back to back. Desktop
 * (`md:` and up) is unaffected — everything shows unconditionally there,
 * same as before.
 */
export function ProjectFolder({
  project,
  reverse = false,
  onAssetClick,
}: {
  project: PortfolioProject
  /** Alternates media/text sides per project, matching the Stitch portfolio's zigzag layout. */
  reverse?: boolean
  onAssetClick: (image: string, alt: string) => void
}) {
  const { t, lang } = useLanguage()
  const [mobileOpen, setMobileOpen] = useState(false)
  const tagColorClasses =
    project.tagColor === 'cyan'
      ? 'text-accent-cyan border-accent-cyan/40'
      : 'text-accent-amber-light border-accent-amber/40'

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-6 md:grid-cols-[3fr_2fr] md:items-center md:gap-10">
        <GlassCard
          className={`overflow-hidden p-2 md:order-none ${reverse ? 'md:order-2' : ''}`}
        >
          <div className="overflow-hidden rounded-md">
            {project.beforeAfter ? (
              <BeforeAfterSlider
                before={project.beforeAfter.before}
                after={project.beforeAfter.after}
                beforeAlt={`${project.name} — ${t('ba-before')}`}
                afterAlt={`${project.name} — ${t('ba-after')}`}
              />
            ) : (
              <div className="flex aspect-[16/11] w-full items-center justify-center bg-black/20">
                <img
                  src={project.thumb}
                  alt={project.name}
                  loading="lazy"
                  width={project.thumbWidth}
                  height={project.thumbHeight}
                  className="max-h-full max-w-full rounded-md object-contain"
                />
              </div>
            )}
          </div>
        </GlassCard>

        <div className={`flex flex-col gap-3 ${reverse ? 'md:order-1' : ''}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-block w-fit rounded-pill border bg-transparent px-3 py-1 text-xs font-semibold ${tagColorClasses}`}
            >
              {lang === 'en' ? project.tagEn : project.tag}
            </span>
            <a
              href={project.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-fit items-center gap-1 text-xs font-semibold text-accent-fire transition-colors hover:text-accent-amber-light md:hidden"
            >
              <ExternalLink size={12} /> {project.externalLabel}
            </a>
          </div>
          <h3 className="font-serif text-2xl font-semibold text-foreground md:text-3xl">{project.name}</h3>

          {/* Mobile-only toggle — everything below stays open/unconditional on md+. */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-expanded={mobileOpen}
            className="flex w-fit items-center gap-1.5 text-sm font-semibold text-accent-fire md:hidden"
          >
            {mobileOpen ? t('pf-collapse') : t('pf-expand')}
            <ChevronDown size={16} className={`transition-transform duration-normal ${mobileOpen ? 'rotate-180' : ''}`} />
          </button>

          <div className={`${mobileOpen ? 'flex' : 'hidden'} flex-col gap-3 md:flex`}>
            <p className="text-sm leading-relaxed text-foreground-muted">
              {lang === 'en' ? project.descEn : project.desc}
            </p>
            <div className="flex flex-wrap gap-2">
              {(lang === 'en' ? project.chipsEn : project.chips).map((chip) => (
                <span
                  key={chip}
                  className="rounded-pill border border-white/10 bg-white/[0.03] px-3 py-1 text-xs text-foreground-subtle"
                >
                  {chip}
                </span>
              ))}
            </div>
            <a
              href={project.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-accent-fire transition-colors hover:text-accent-amber-light"
            >
              <ExternalLink size={14} /> {project.externalLabel}
            </a>
            <Link
              to={`/${lang}/portfolio/${project.id}`}
              className="inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-foreground-muted transition-colors hover:text-accent-fire"
            >
              {t('pf-case-study-link')}
            </Link>

            <CaseStudyAccordion project={project} />
          </div>
        </div>
      </div>

      <MediaTabs tabs={project.tabs} onAssetClick={onAssetClick} />
    </div>
  )
}
